import {
  Children,
  cloneElement,
  isValidElement,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useEmbedTokens } from "../theme";

interface Props {
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: string;
  help?: string;
  children: ReactNode;
}

// Inject `aria-describedby={htmlFor}-err` into the single control child when an
// error is shown, so EVERY consumer gets the control↔error association for free
// (no per-call-site wiring). Centralization rules:
//   - Only runs when `error` is present (no dangling reference to an absent id).
//   - PRESERVES a child that already sets `aria-describedby` explicitly — it is
//     NOT overwritten and NOT appended to, so a consumer that wires its own id
//     (e.g. FormPreview, which sets the identical `${id}-err`) never collides or
//     double-wires. Idempotent by construction.
//   - No-ops unless `children` is exactly one valid element (multiple children
//     or plain text are returned untouched); the wrapper still renders the error
//     span, just without auto-association.
//   - Reaches the child DIRECTLY and never through `Children.toArray`. That is
//     the whole of the fix below and it is not a style preference:
//     `Children.toArray` assigns SYNTHETIC KEYS (".0"), so the rendered
//     element's key flipped between `null` (no error) and ".0" (error). React
//     treats a changed key as a different element and REMOUNTS it. Measured
//     2026-09-05 with a two-state harness: the control was a different DOM node
//     either side of an error toggle, and `document.activeElement` fell back to
//     `<body>`. The customer-visible failure is that clearing an error ON
//     CHANGE — which every consumer here does, and which the payment form's
//     progressive validation depends on — destroyed the input mid-keystroke, so
//     the rest of what was typed went nowhere. Association and remounting were
//     coupled only by the traversal helper; nothing needed toArray.
function withErrorDescription(children: ReactNode, errId: string): ReactNode {
  if (Children.count(children) !== 1) return children;
  if (!isValidElement(children)) return children;
  const existing = (children.props as { "aria-describedby"?: string })["aria-describedby"];
  if (existing != null) return children; // consumer already wired it — don't touch
  return cloneElement(children, { "aria-describedby": errId } as Record<string, unknown>);
}

// A labelled form-control wrapper: <label> (with a `*` when required), the
// control (`children`), then help OR error copy below. `error` takes
// precedence and hides `help`. When `error` is set, the control child is
// associated to the error span via aria-describedby (see withErrorDescription).
export function EmbedField({ label, htmlFor, required = false, error, help, children }: Props) {
  const t = useEmbedTokens();
  const errId = htmlFor ? `${htmlFor}-err` : undefined;
  const wrapper: CSSProperties = { marginBottom: t.spacing.md };
  const labelStyle: CSSProperties = {
    display: "block", fontSize: t.fontSize.sm, fontWeight: 500,
    color: t.colors.text, marginBottom: 6,
  };
  const hintBase: CSSProperties = { display: "block", fontSize: t.fontSize.xs, marginTop: 6 };
  const control = error && errId ? withErrorDescription(children, errId) : children;
  return (
    <div style={wrapper}>
      <label htmlFor={htmlFor} style={labelStyle}>
        {label}
        {required && <span style={{ color: t.colors.error }}>{" *"}</span>}
      </label>
      {control}
      {error ? (
        <span
          id={errId}
          role="alert"
          style={{ ...hintBase, color: t.colors.error }}
        >
          {error}
        </span>
      ) : help ? (
        <span
          id={htmlFor ? `${htmlFor}-help` : undefined}
          style={{ ...hintBase, color: t.colors.textSecondary }}
        >
          {help}
        </span>
      ) : null}
    </div>
  );
}
